import React, { useEffect, useState } from "react";
import { Shell, Section } from "@/components/loomi/Layout";
import { PANEL_NAV } from "./PanelDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ChangePasswordCard from "@/components/loomi/ChangePasswordCard";

export default function PanelProfile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ country: "", age: "", gender: "", interests: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/panel/me");
      setMe(data);
      const p = data.profile || {};
      setForm({
        country: p.country || "",
        age: p.age || "",
        gender: p.gender || "",
        interests: (p.interests || []).join(", "),
      });
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = {
        country: form.country || null,
        age: form.age ? Number(form.age) : null,
        gender: form.gender || null,
        interests: form.interests ? form.interests.split(",").map((s) => s.trim()).filter(Boolean) : null,
      };
      await api.patch("/panel/profile", body);
      toast.success("Profile saved");
    } catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
    finally { setSaving(false); }
  };

  return (
    <Shell title="Your profile" portalLabel="Panel" nav={PANEL_NAV}>
      <div className="grid md:grid-cols-3 gap-6">
        <Section title="Account" testId="section-panel-account">
          <div className="space-y-3 text-sm">
            <Row k="Name" v={me?.name} />
            <Row k="Email" v={me?.email} mono />
            <Row k="Points balance" v={me?.points_balance} />
            <Row k="Joined" v={me ? new Date(me.created_at).toLocaleDateString() : "—"} />
          </div>
        </Section>
        <form onSubmit={save} className="md:col-span-2 glass rounded-2xl p-6" data-testid="panel-profile-form">
          <h3 className="font-display font-bold text-lg mb-4">Demographics</h3>
          <div className="grid grid-cols-2 gap-4">
            <F label="Country"><input data-testid="panel-profile-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="in" /></F>
            <F label="Age"><input data-testid="panel-profile-age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="in" /></F>
            <F label="Gender">
              <select data-testid="panel-profile-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="in">
                <option value="">Prefer not to say</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="non-binary">Non-binary</option>
              </select>
            </F>
            <F label="Interests (comma-separated)"><input data-testid="panel-profile-interests" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} className="in" /></F>
          </div>
          <button disabled={saving} data-testid="panel-profile-save" className="mt-5 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 rounded-md px-4 py-2 text-sm font-medium">{saving ? "Saving…" : "Save profile"}</button>
          <style>{`.in{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:8px 12px;font-size:14px;color:#fff}
.in:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 1px #6366f1}`}</style>
        </form>
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-start-2 md:col-span-2">
          <ChangePasswordCard testPrefix="panel-profile" isGoogleOnly={me?.auth_provider === "google"} />
        </div>
      </div>
    </Shell>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-2">
      <div className="text-[10px] uppercase tracking-widest text-mute">{k}</div>
      <div className={`text-right ${mono ? "font-mono text-xs" : ""}`}>{v ?? "—"}</div>
    </div>
  );
}
function F({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">{label}</label>
      {children}
    </div>
  );
}
